import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Loader2, Sparkles, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { absoluteUrl, getPublicTool } from "@/lib/tools-catalog";
import { buildToolSoftwareApplicationJsonLd } from "@/lib/seo/tool-structured-data";

export const Route = createFileRoute("/tools/humaniser")({
  head: () => {
    const tool = getPublicTool("humaniser");
    const url = absoluteUrl(tool.path);
    const title = tool.pageTitle;
    const description = tool.description;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildToolSoftwareApplicationJsonLd(tool)),
        },
      ],
    };
  },
  component: HumaniserPage,
});

type Mode = "humanise" | "grammar" | "derepeat";
type Tone = "academic" | "professional" | "casual";

function HumaniserPage() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<Mode>("humanise");
  const [tone, setTone] = useState<Tone>("professional");
  const [loading, setLoading] = useState(false);

  const words = useMemo(
    () => (input.trim() ? input.trim().split(/\s+/).length : 0),
    [input],
  );
  const overFree = words > 300;

  async function run() {
    if (words < 4) {
      toast.error("Paste at least a short paragraph");
      return;
    }
    setLoading(true);
    setOutput("");
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      const res = await fetch("/api/public/tools/humanise", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text: input, mode, tone }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.error || "Failed");
        return;
      }
      setOutput(data.text);
      if (data.billed) toast.success(`Done — 1 credit used. Balance: ${data.balance ?? "—"}`);
      else toast.success("Done");
    } catch (e: any) {
      toast.error(e?.message || "Network error");
    } finally {
      setLoading(false);
    }
  }

  function copyOut() {
    navigator.clipboard.writeText(output);
    toast.success("Copied");
  }

  function downloadOut() {
    const blob = new Blob([output], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "humanised.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="container-narrow py-12">
      <p className="text-xs uppercase tracking-[0.25em] text-gold">Free tool</p>
      <h1 className="mt-2 font-display text-3xl text-navy md:text-4xl">AI Humaniser</h1>
      <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
        Paste text below. Up to 300 words is free — longer rewrites cost 1 credit (sign in required).
        Your citations, names and section numbers are preserved.
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div>
          <div className="mb-2 flex flex-wrap gap-2 text-xs">
            {(["humanise", "grammar", "derepeat"] as Mode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`rounded-full border px-3 py-1 capitalize ${
                  mode === m
                    ? "border-navy bg-navy text-white"
                    : "border-input bg-background text-muted-foreground"
                }`}
              >
                {m === "derepeat" ? "Remove repetition" : m}
              </button>
            ))}
          </div>
          {mode === "humanise" && (
            <div className="mb-2 flex flex-wrap gap-2 text-xs">
              {(["academic", "professional", "casual"] as Tone[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTone(t)}
                  className={`rounded-full border px-3 py-1 capitalize ${
                    tone === t
                      ? "border-gold bg-gold text-navy"
                      : "border-input bg-background text-muted-foreground"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={16}
            maxLength={5000}
            placeholder="Paste your text here…"
            className="w-full rounded-md border border-input bg-background p-3 text-sm"
          />
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {words} words {overFree && <span className="text-amber-700">· 1 credit</span>}
            </span>
            <button
              onClick={run}
              disabled={loading || words < 4}
              className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Rewrite
            </button>
          </div>
        </div>

        <div>
          <label className="mb-2 block text-xs uppercase tracking-widest text-muted-foreground">
            Output
          </label>
          <textarea
            value={output}
            readOnly
            rows={16}
            placeholder="The rewritten text will appear here."
            className="w-full rounded-md border border-input bg-muted/30 p-3 text-sm"
          />
          {output && (
            <div className="mt-2 flex gap-2">
              <button
                onClick={copyOut}
                className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-2 text-xs"
              >
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
              <button
                onClick={downloadOut}
                className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-2 text-xs"
              >
                <Download className="h-3.5 w-3.5" /> Download .txt
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
